import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./features.css";
import logoMetagent from "../../assets/metagent-logo-text.webp";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

// UTIL METHOD
function wrapWordsInSpan(element) {
  const processNode = (node, highlight) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const words = node.textContent.split(" ");
      const fragment = document.createDocumentFragment();
      words.forEach((word, index) => {
        const wrapper = document.createElement("span");
        wrapper.className = `word${highlight ? " feature-highlight" : ""}`;
        const inner = document.createElement("span");
        inner.textContent = word;
        wrapper.appendChild(inner);
        fragment.appendChild(wrapper);
        if (index < words.length - 1) {
          fragment.appendChild(document.createTextNode(" "));
        }
      });
      node.replaceWith(fragment);
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList.contains("word")) return;
      const isHighlight = highlight || node.classList.contains("feature-highlight");
      Array.from(node.childNodes).forEach((child) => processNode(child, isHighlight));
    }
  };

  processNode(element, false);
}

function FeaturesTop({ title, logo, isMobile }) {
  if (!isMobile) {
    return (
      <div className="top">
        <p dangerouslySetInnerHTML={{ __html: title }} />
        <img src={logo} alt="Metagent Logo" className="logo" />
      </div>
    );
  }

  return (
    <div className="top">
      <p dangerouslySetInnerHTML={{ __html: title }} />
      <img src={logo} alt="Metagent Logo" className="logo" />
    </div>
  );
}

function FeaturesParagraphs({ paragraphs, isMobile }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const slideRef = useRef(null);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % paragraphs.length);
  };

  if (!isMobile) {
    return (
      <div className="paragraphs">
        {paragraphs.map((text, index) => (
          <p key={index} className="paragraph" dangerouslySetInnerHTML={{ __html: text }} />
        ))}
      </div>
    );
  }

  // Mobile: Horizontal slide swap
  return (
    <div className="paragraphs-mobile">
      <div className="paragraphs-slider" ref={slideRef}>
        <div 
          className="paragraphs-track"
          style={{ 
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: 'transform 0.4s ease-in-out'
          }}
        >
          {paragraphs.map((text, index) => (
            <div key={index} className="paragraph-slide">
              <p className="paragraph" dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          ))}
        </div>
      </div>
      <button 
        className="paragraphs-next-btn"
        onClick={handleNext}
        aria-label="Next slide"
      >
        →
      </button>
      <div className="paragraphs-indicator">
        {currentIndex + 1} / {paragraphs.length}
      </div>
    </div>
  );
}

export default function Features() {
  const rootRef = useRef(null);
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    // Check if mobile (disable complex GSAP animations on mobile)
    if (isMobile) {
      // Mobile: Framer Motion will handle animations
      return;
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
    const root = rootRef.current;
    const pinHeight = root.querySelector(".pin-height");
    const container = root.querySelector(".container");
    const paragraphs = root.querySelectorAll(".paragraph");

    paragraphs.forEach((paragraph) => {
      wrapWordsInSpan(paragraph);
    });

    ScrollTrigger.create({
      trigger: pinHeight, // Listens to pin-height
      start: "top top",
      end: "bottom bottom",
      pin: container, // The pinned section
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        // All tweens of my timeline will have the same scrollTrigger properties
        trigger: pinHeight,
        start: "top top",
        end: "bottom bottom",
        scrub: true, // Progresses with the scroll
      },
    });

    paragraphs.forEach((paragraph, index) => {
      if (paragraphs[index + 1]) {
        // Check if there is a next paragraph
        tl.to(paragraphs[index].querySelectorAll(".word span"), {
          y: "100%", // Disappearance of words from paragraphs[index]
          duration: 1,
          stagger: 0.2,
          ease: "power4.in",
        }); // Both tweens will play at the same time
        tl.to(
          paragraphs[index + 1].querySelectorAll(".word span"),
          {
            y: "0%", // Appearance of words from paragraphs[index+1]
            duration: 1,
            delay: 1,
            stagger: 0.2,
            ease: "power4.out",
          },
          "<"
        ); // This means the animation starts at the beginning of the previous tween
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [isMobile]);

  const paragraphs = [
    tr(
      "Metagent is not a one-size-fits all AI. <span class=\"feature-highlight\">Each AI agent can be customized</span> to match your workflow, tools, and priorities, so it works the way you already work, not the other way around.",
      "Metagent bukan AI satu‑untuk‑semua. <span class=\"feature-highlight\">Setiap AI agent bisa dikustomisasi</span> agar sesuai workflow, tools, dan prioritasmu, sehingga AI bekerja mengikuti caramu."
    ),
    tr(
      "While <span class=\"feature-highlight\">Metagent offers powerful core capabilities</span>, it doesn't stop there. Your AI agent can be extended, adapted, and trained for specific tasks, roles, or business needs, without being limited to a fixed feature set.",
      "Walau <span class=\"feature-highlight\">Metagent menawarkan kapabilitas inti yang kuat</span>, itu bukan batasnya. AI agent bisa diperluas, diadaptasi, dan dilatih untuk tugas, role, atau kebutuhan bisnis yang spesifik."
    ),
    tr(
      "As your work evolves, your <span class=\"feature-highlight\">AI agent evolves with you</span>. Metagent grows from handling simple tasks to managing complex workflows, ensuring long-term value without adding operational complexity.",
      "Seiring kerja kamu berkembang, <span class=\"feature-highlight\">AI agent ikut berkembang</span>. Metagent bertumbuh dari tugas sederhana hingga mengelola workflow kompleks tanpa menambah kerumitan."
    ),
  ];

  return (
    <section id="features" className="mwg_effect022" ref={rootRef}>
      <div className="pin-height">
        <div className="container">
          <FeaturesTop
            title={tr("Your AI Agent,  <br /> Your Rules", "AI Agent‑mu,  <br /> Aturan‑mu")}
            logo={logoMetagent}
            isMobile={isMobile}
          />
          <FeaturesParagraphs
            paragraphs={paragraphs}
            isMobile={isMobile}
          />
        </div>
      </div>
    </section>
  );
}

